import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Terminal, Monitor, CheckCircle, Copy, Cpu, Zap, AudioWaveform, ArrowRight, Apple, Menu, ChevronDown, LogOut, Server } from 'lucide-react';

let logo;
try {
  logo = require('./images/logo.png');
} catch (e) {
  logo = null;
}

const DownloadPage = () => {
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const rootElement = document.getElementById('root');

    const originalHtmlOverflow = htmlElement.style.overflow;
    const originalBodyOverflow = bodyElement.style.overflow;
    const originalRootOverflow = rootElement ? rootElement.style.overflow : '';

    htmlElement.style.overflow = 'auto';
    bodyElement.style.overflow = 'auto';

    const savedTheme = htmlElement.getAttribute('data-theme') || 'dark';
    htmlElement.setAttribute('data-theme', 'dark');
    bodyElement.setAttribute('data-theme', 'dark');

    return () => {
      htmlElement.style.overflow = originalHtmlOverflow;
      bodyElement.style.overflow = originalBodyOverflow;
      htmlElement.setAttribute('data-theme', savedTheme);
      bodyElement.setAttribute('data-theme', savedTheme);
    };
  }, []);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const aptCommands = `# Add GPG key
curl -fsSL https://chipdesigner.ai/apt/chipdesigner.gpg | sudo gpg --dearmor -o /usr/share/keyrings/chipdesigner.gpg

# Add repository
echo "deb [signed-by=/usr/share/keyrings/chipdesigner.gpg] https://chipdesigner.ai/apt stable main" | sudo tee /etc/apt/sources.list.d/chipdesigner.list

# Install
sudo apt update && sudo apt install chipdesigner-ide`;

  const directInstall = `wget https://chipdesigner.ai/downloads/linux/chipdesigner-ide-latest-amd64.deb
sudo dpkg -i chipdesigner-ide-latest-amd64.deb
sudo apt-get install -f`;

  const dnfCommands = `# Add repository
sudo dnf config-manager --add-repo https://chipdesigner.ai/downloads/redhat/chipdesigner-ide.repo

# Install
sudo dnf install chipdesigner-ide`;

  const rpmDirectInstall = `wget https://chipdesigner.ai/downloads/redhat/chipdesigner-ide-latest-x86_64.rpm
sudo dnf install ./chipdesigner-ide-latest-x86_64.rpm`;

  const renderCommandLines = (text) =>
    text.split('\n').map((line, i) => {
      if (line.trim() === '') return <div key={i} className="h-2" />;
      const isComment = line.trim().startsWith('#');
      return (
        <div
          key={i}
          className={`whitespace-pre-wrap break-all ${isComment ? 'text-gray-500' : 'text-gray-200'}`}
        >
          {line}
        </div>
      );
    });

  const features = [
    { icon: Cpu, label: 'AI-Powered RTL Generation' },
    { icon: Zap, label: 'Integrated Simulation' },
    { icon: AudioWaveform, label: 'Waveform Viewer' },
    { icon: Terminal, label: 'Terminal & Tools' },
  ];

  return (
    <div className="hero-ref-bg text-slate-100 min-h-screen relative page-transition">
      {/* Ambient glows matching landing page */}
      <div className="absolute top-10 left-1/4 w-[500px] h-[300px] bg-blue-600/10 blur-[100px] rounded-full pointer-events-none" />
      <div className="absolute top-20 right-10 w-[600px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />

      {/* Navigation — same as landing page */}
      <nav className="fixed top-0 w-full header-ref-nav z-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Left Brand: elinnos | chipdesigner.ai */}
            <div className="flex items-center space-x-3">
              {logo ? (
                <a href="/" className="hover:opacity-80 transition-opacity flex items-center gap-2">
                  <img src={logo} alt="Elinnos" className="h-12 w-auto" />
                </a>
              ) : (
                <a href="/" className="text-xl font-extrabold tracking-tight text-white hover:text-cyan-400 transition-colors">
                  elinnos
                </a>
              )}
              <span className="text-slate-600 font-light text-lg">|</span>
              <a href="/" className="text-base sm:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 hover:opacity-80 transition-opacity">
                chipdesigner.ai
              </a>
            </div>

            {/* Center Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/#features" className="nav-tab">Features</a>
              <a href="/#demo" className="nav-tab">Platform</a>
              <a href="/dashboard" className="nav-tab">Dashboard</a>
              <a href="/#contact" className="nav-tab">Contact</a>
            </div>

            {/* Right User Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <a
                href="/login"
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all cursor-pointer"
                style={{ border: 'none' }}
              >
                Sign In
              </a>
            </div>

            <div className="md:hidden">
              <button className="text-slate-300 hover:text-white">
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-ref-bg circuit-trace-glow relative pt-28 sm:pt-32 pb-12 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="dl-fade-up inline-flex items-center gap-2 px-4 py-1.5 rounded-full dl-feature-pill mb-6">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm font-medium text-cyan-400">v1.112.49 — Now available</span>
          </div>
          <h1 className="dl-fade-up-delay-1 text-5xl md:text-6xl font-bold tracking-tight text-white mb-6">
            Download <span className="gradient-text">ChipDesigner</span>
          </h1>
          <p className="dl-fade-up-delay-2 text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Professional VLSI design environment with AI-powered assistance,
            waveform viewer, and integrated simulation tools.
          </p>

          {/* Feature pills */}
          <div className="dl-fade-up-delay-3 flex flex-wrap justify-center gap-3 mt-8">
            {features.map((f, i) => (
              <div key={i} className="dl-feature-pill inline-flex items-center gap-2 px-4 py-2 rounded-full">
                <f.icon className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-medium text-slate-300">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Cards */}
      <section className="py-12 relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Linux Download */}
            <div className="dl-card dl-fade-up-delay-2 rounded-3xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="dl-icon-badge w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.12), rgba(234, 88, 12, 0.08))' }}>
                  <Terminal className="w-7 h-7 text-orange-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Linux</h2>
                  <p className="text-sm text-slate-400">Debian, Ubuntu, and derivatives</p>
                </div>
              </div>

              {/* Direct Download Button */}
              <a
                href="/downloads/linux/chipdesigner-ide-latest-amd64.deb"
                className="dl-btn-gradient inline-flex items-center gap-3 text-white font-semibold px-8 py-4 rounded-2xl mb-6 w-full justify-center"
              >
                <Download className="w-5 h-5" />
                Download .deb (amd64)
              </a>

              {/* APT Repository Section */}
              <div className="pt-6 border-t border-slate-700/50">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-semibold text-white">
                    Recommended: APT Repository
                  </h3>
                </div>
                <p className="text-xs text-slate-400 mb-3">
                  Automatic updates & dependency management
                </p>

                <div className="relative">
                  <div className="dl-code-block p-4 pr-12 text-xs font-mono leading-relaxed">
                    {renderCommandLines(aptCommands)}
                  </div>
                  <button
                    onClick={() => copyToClipboard(aptCommands, 'apt')}
                    className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Copy to clipboard"
                  >
                    {copied === 'apt' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Direct Install Section */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-white mb-3">
                  Alternative: Direct Download
                </h3>
                <div className="relative">
                  <div className="dl-code-block p-4 pr-12 text-xs font-mono leading-relaxed">
                    {renderCommandLines(directInstall)}
                  </div>
                  <button
                    onClick={() => copyToClipboard(directInstall, 'direct')}
                    className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Copy to clipboard"
                  >
                    {copied === 'direct' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Red Hat Download */}
            <div className="dl-card dl-fade-up-delay-2 rounded-3xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="dl-icon-badge w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(220, 38, 38, 0.08))' }}>
                  <Server className="w-7 h-7 text-red-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Red Hat</h2>
                  <p className="text-sm text-slate-400">RHEL, Fedora, and derivatives</p>
                </div>
              </div>

              {/* Direct Download Button */}
              <a
                href="/downloads/redhat/chipdesigner-ide-latest-x86_64.rpm"
                className="dl-btn-gradient inline-flex items-center gap-3 text-white font-semibold px-8 py-4 rounded-2xl mb-6 w-full justify-center"
              >
                <Download className="w-5 h-5" />
                Download .rpm (x86_64)
              </a>

              {/* DNF Repository Section */}
              <div className="pt-6 border-t border-slate-700/50">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-semibold text-white">
                    Recommended: DNF Repository
                  </h3>
                </div>
                <p className="text-xs text-slate-400 mb-3">
                  Automatic updates & dependency management
                </p>

                <div className="relative">
                  <div className="dl-code-block p-4 pr-12 text-xs font-mono leading-relaxed">
                    {renderCommandLines(dnfCommands)}
                  </div>
                  <button
                    onClick={() => copyToClipboard(dnfCommands, 'dnf')}
                    className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Copy to clipboard"
                  >
                    {copied === 'dnf' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Direct Install Section */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-white mb-3">
                  Alternative: Direct Download
                </h3>
                <div className="relative">
                  <div className="dl-code-block p-4 pr-12 text-xs font-mono leading-relaxed">
                    {renderCommandLines(rpmDirectInstall)}
                  </div>
                  <button
                    onClick={() => copyToClipboard(rpmDirectInstall, 'rpm')}
                    className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Copy to clipboard"
                  >
                    {copied === 'rpm' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Windows Download */}
            <div className="dl-card dl-fade-up-delay-3 rounded-3xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="dl-icon-badge w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(79, 70, 229, 0.08))' }}>
                  <Monitor className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Windows</h2>
                  <p className="text-sm text-slate-400">Windows 10 and 11 (x64)</p>
                </div>
              </div>

              {/* Direct Download Button */}
              <a
                href="/downloads/windows/ChipDesigner-IDE-Setup-latest.exe"
                className="dl-btn-gradient inline-flex items-center gap-3 text-white font-semibold px-8 py-4 rounded-2xl mb-3 w-full justify-center"
              >
                <Download className="w-5 h-5" />
                Download Installer (.exe)
              </a>

              {/* Zip Archive Alternative */}
              <a
                href="/downloads/windows/ChipDesigner-IDE-win32-x64-latest.zip"
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium text-sm transition-colors mb-6"
              >
                <Download className="w-4 h-4" />
                Download portable .zip archive
              </a>

              {/* Installation Instructions */}
              <div className="pt-6 border-t border-slate-700/50">
                <h3 className="text-sm font-semibold text-white mb-3">
                  Installation Instructions
                </h3>
                <ol className="space-y-2.5 text-sm text-slate-400">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-900/40 text-cyan-400 text-xs font-bold flex items-center justify-center">1</span>
                    <span>Download the installer (.exe) above</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-900/40 text-cyan-400 text-xs font-bold flex items-center justify-center">2</span>
                    <span>Run <code className="text-cyan-400 font-mono text-xs">ChipDesigner-IDE-Setup-latest.exe</code></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-900/40 text-cyan-400 text-xs font-bold flex items-center justify-center">3</span>
                    <span>Follow the setup wizard to install</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-900/40 text-cyan-400 text-xs font-bold flex items-center justify-center">4</span>
                    <span>Launch <strong className="text-white">ChipDesigner IDE</strong> from the Start menu</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* macOS Download */}
          <div className="dl-card dl-fade-up-delay-4 rounded-3xl p-8 mt-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="dl-icon-badge w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(168, 168, 168, 0.12), rgba(120, 120, 120, 0.08))' }}>
                <Apple className="w-7 h-7 text-slate-300" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">macOS</h2>
                <p className="text-sm text-slate-400">Apple Silicon (M1/M2/M3/M4)</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                {/* Direct Download Button - DMG */}
                <a
                  href="/downloads/macos/ChipDesigner-IDE-darwin-arm64-latest.dmg"
                  className="dl-btn-gradient inline-flex items-center gap-3 text-white font-semibold px-8 py-4 rounded-2xl mb-3 w-full justify-center"
                >
                  <Download className="w-5 h-5" />
                  Download (.dmg)
                </a>

                {/* Zip Archive Alternative */}
                <a
                  href="/downloads/macos/ChipDesigner-IDE-darwin-arm64-latest.zip"
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium text-sm transition-colors mb-3 w-full justify-center"
                >
                  <Download className="w-4 h-4" />
                  Download portable .zip archive
                </a>

                <p className="text-xs text-slate-400 text-center">
                  Apple Silicon (M1/M2/M3/M4)
                </p>
              </div>

              {/* Installation Instructions */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">
                  Installation Instructions
                </h3>
                <ol className="space-y-2.5 text-sm text-slate-400">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-700/40 text-slate-300 text-xs font-bold flex items-center justify-center">1</span>
                    <span>Download the .dmg file above</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-700/40 text-slate-300 text-xs font-bold flex items-center justify-center">2</span>
                    <span>Open the .dmg and drag <strong className="text-white">ChipDesigner IDE</strong> to Applications</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-700/40 text-slate-300 text-xs font-bold flex items-center justify-center">3</span>
                    <span>Launch from Launchpad or Applications folder</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* After Installation */}
      <section className="py-16 relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="dl-card rounded-3xl p-8">
            <h2 className="text-2xl font-bold text-white mb-2">After Installation</h2>
            <p className="text-slate-400 text-sm mb-6">
              Launch the IDE and bundled tools on Linux using these commands:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { cmd: 'chipdesigner-ide', desc: 'Launch the IDE' },
                { cmd: 'cder', desc: 'IDE shortcut' },
                { cmd: 'elwave', desc: 'Waveform viewer' },
                { cmd: 'pulse', desc: 'HDL simulation' },
              ].map((item, i) => (
                <div key={i} className="dl-feature-pill rounded-xl p-4">
                  <code className="text-cyan-400 font-mono text-sm font-semibold">{item.cmd}</code>
                  <p className="text-slate-400 text-xs mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* System Requirements */}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {['Ubuntu 20.04+', 'Debian 11+', 'RHEL 8+', 'Fedora 40+', 'Windows 10+', '4GB RAM', '2GB disk'].map((req, i) => (
              <span key={i} className="dl-feature-pill px-4 py-1.5 rounded-full text-xs font-medium text-slate-400">
                {req}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Footer — same as landing page */}
      <footer className="border-t border-cyan-500/10 text-white py-6 relative z-10" style={{ background: 'linear-gradient(to top, rgba(10, 16, 32, 0.8) 0%, rgba(10, 16, 32, 0.3) 70%, transparent 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {logo && <img src={logo} alt="Elinnos" className="h-6 w-auto" />}
              <span className="text-sm font-bold gradient-text">chipdesigner.ai</span>
            </div>

            <div className="flex items-center gap-6">
              <a href="/#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a>
              <a href="/#demo" className="text-sm text-slate-400 hover:text-white transition-colors">Demo</a>
              <a href="https://elinnos.com" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white transition-colors">Elinnos</a>
              <a href="mailto:contact@chipdesigner.ai" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</a>
            </div>
          </div>

          <div className="border-t border-cyan-500/10 mt-4 pt-3 text-center">
            <p className="text-xs text-slate-500">&copy; 2026 chipdesigner.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DownloadPage;
